import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  OutreachBoardResponseSchema,
  OutreachListResponseSchema,
  OutreachSchema,
  RhythmResponseSchema,
  type Outreach,
  type OutreachBoardResponse,
  type OutreachFormValues,
  type OutreachStatus,
  type OutreachTier,
  type RhythmResponse,
} from "@/api/types/outreach";

/** Query-key factory — mirrors the events/quotes hook convention. */
export const outreachKeys = {
  all: ["outreach"] as const,
  lists: () => [...outreachKeys.all, "list"] as const,
  list: (params: {
    tier?: OutreachTier;
    status?: OutreachStatus;
    verified?: boolean;
    q?: string;
    limit?: number;
  }) => [...outreachKeys.lists(), params] as const,
  details: () => [...outreachKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...outreachKeys.details(), id] as const,
  board: () => [...outreachKeys.all, "board"] as const,
  rhythm: () => [...outreachKeys.all, "rhythm"] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: outreachKeys.all });
}

interface UseOutreachListParams {
  tier?: OutreachTier;
  status?: OutreachStatus;
  verified?: boolean;
  q?: string;
  limit?: number;
}

/** List query with cursor pagination, mirroring useInquiries' convention. */
export function useOutreachList(params: UseOutreachListParams = {}) {
  const { tier, status, verified, q, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: outreachKeys.list({ tier, status, verified, q, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (tier) searchParams.set("tier", tier);
      if (status) searchParams.set("status", status);
      if (verified !== undefined) searchParams.set("verified", String(verified));
      if (q?.trim()) searchParams.set("q", q.trim());
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/outreach?${searchParams.toString()}`);
      return validateResponse(OutreachListResponseSchema, data, "GET /v2/outreach");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 60 * 1000,
  });
}

export function useOutreachDetail(id: string | undefined) {
  return useQuery<Outreach>({
    queryKey: outreachKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/outreach/${id}`);
      return validateResponse(OutreachSchema, data, "GET /v2/outreach/:id");
    },
    enabled: !!id,
  });
}

/** Kanban board projection — {columns:[{status, items}]}. */
export function useOutreachBoard() {
  return useQuery<OutreachBoardResponse>({
    queryKey: outreachKeys.board(),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/outreach/board`);
      return validateResponse(OutreachBoardResponseSchema, data, "GET /v2/outreach/board");
    },
    staleTime: 15 * 1000,
  });
}

/** The weekly rhythm quota widgets. */
export function useOutreachRhythm() {
  return useQuery<RhythmResponse>({
    queryKey: outreachKeys.rhythm(),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/outreach/rhythm`);
      return validateResponse(RhythmResponseSchema, data, "GET /v2/outreach/rhythm");
    },
    staleTime: 15 * 1000,
  });
}

export function useCreateOutreach() {
  const queryClient = useQueryClient();
  return useMutation<Outreach, unknown, OutreachFormValues>({
    mutationFn: async (input) => {
      // API expects `contact_name` (creates the contact inline); the form
      // collects it as `name`.
      const { name, ...rest } = input;
      const { data } = await apiClient.post("/v2/outreach", { ...rest, contact_name: name });
      return validateResponse(OutreachSchema, data, "POST /v2/outreach");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export interface OutreachPatchInput {
  id: string;
  status?: OutreachStatus;
  next_touch_at?: string | null;
  vendor_list_status?: string | null;
  verified?: boolean;
  notes?: string | null;
}

/**
 * Generic patch — covers status transitions (including drag-and-drop on the
 * board), next-touch scheduling, vendor-list status, verified toggle, and
 * notes. Optimistically moves the card between board columns on a status
 * change so the kanban drag feels instant, mirroring
 * useUpdateInquiryStage's rollback-on-error pattern.
 */
export function usePatchOutreach() {
  const queryClient = useQueryClient();
  return useMutation<Outreach, unknown, OutreachPatchInput, { previous?: OutreachBoardResponse }>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/outreach/${id}`, patch);
      return validateResponse(OutreachSchema, data, "PATCH /v2/outreach/:id");
    },
    onMutate: async ({ id, status }) => {
      if (!status) return {};
      await queryClient.cancelQueries({ queryKey: outreachKeys.board() });
      const previous = queryClient.getQueryData<OutreachBoardResponse>(outreachKeys.board());

      if (previous) {
        let moved: OutreachBoardResponse["columns"][number]["items"][number] | undefined;
        const withoutItem = previous.columns.map((col) => {
          const found = col.items.find((item) => item.id === id);
          if (found) moved = found;
          return { ...col, items: col.items.filter((item) => item.id !== id) };
        });

        const next: OutreachBoardResponse = {
          columns: withoutItem.map((col) =>
            col.status === status && moved
              ? { ...col, items: [{ ...moved, status }, ...col.items] }
              : col,
          ),
        };

        queryClient.setQueryData(outreachKeys.board(), next);
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(outreachKeys.board(), context.previous);
      }
      invalidateAll(queryClient);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(outreachKeys.detail(updated.id), updated);
    },
    onSettled: () => invalidateAll(queryClient),
  });
}

export function useLogTouch() {
  const queryClient = useQueryClient();
  return useMutation<Outreach, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(`/v2/outreach/${id}/log-touch`);
      return validateResponse(OutreachSchema, data, "POST /v2/outreach/:id/log-touch");
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(outreachKeys.detail(updated.id), updated);
      invalidateAll(queryClient);
    },
  });
}

export function useVerifyOutreach() {
  const queryClient = useQueryClient();
  return useMutation<Outreach, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(`/v2/outreach/${id}/verify`);
      return validateResponse(OutreachSchema, data, "POST /v2/outreach/:id/verify");
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(outreachKeys.detail(updated.id), updated);
      invalidateAll(queryClient);
    },
  });
}

